import * as React from 'react'
import {
	data as json,
	type HeadersFunction,
	type MetaFunction,
} from 'react-router'
import { Grid } from '#app/components/grid.tsx'
import {
	BehanceIcon,
	CodepenIcon,
	DribbbleIcon,
	GithubIcon,
	GlobeIcon,
	InstagramIcon,
	LinkedInIcon,
	TwitchIcon,
	XIcon,
} from '#app/components/icons.tsx'
import { ButtonLink } from '#app/components/button.tsx'
import { HeaderSection } from '#app/components/sections/header-section.tsx'
import {
	HeroSection,
	getHeroImageProps,
} from '#app/components/sections/hero-section.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { H2, H3, H4, H6, Paragraph } from '#app/components/typography.tsx'
import { getImageBuilder, getImgProps, images } from '#app/images.tsx'
import { shuffle } from '#app/utils/cjs/lodash.ts'
import { getPeople } from '#app/utils/credits.server.ts'
import { externalLinks } from '#app/external-links.tsx'
import { getOrigin, reuseUsefulLoaderHeaders } from '#app/utils/misc.ts'
import { type SerializeFrom } from '#app/utils/serialize-from.ts'
import { type Route } from './+types/credits'

export async function loader({ request }: Route.LoaderArgs) {
	const people = await getPeople({ request })
	const domain = new URL(
		getOrigin({ origin: new URL(request.url).origin, path: '' }),
	).host
	const socialMetas = (
		await import('#app/og/page-meta.server.ts')
	).buildPageSocialMetasForRequest(request, {
		title: `Who built ${domain}`,
		description: `It took a team of people to create ${domain}. This page will tell you a little bit about them.`,
		socialImage: {
			kind: 'social-preview',
			preTitle: 'Check out these people',
			title: `The fantastic people who built ${domain}`,
			featuredImage: images.kentCodingOnCouch.id,
		},
	})

	return json(
		{ people: shuffle(people), socialMetas },
		{
			headers: {
				'Cache-Control': 'public, max-age=3600',
				Vary: 'Cookie',
			},
		},
	)
}

export const headers: HeadersFunction = reuseUsefulLoaderHeaders

export const meta: MetaFunction<typeof loader> = ({ data }) =>
	data?.socialMetas ?? []

type Person = SerializeFrom<typeof loader>['people'][number]
type Socials = keyof Omit<
	Person,
	'id' | 'name' | 'role' | 'mediaId' | 'description'
>

const icons = {
	website: <GlobeIcon title="Website" />,
	github: <GithubIcon />,
	x: <XIcon />,
	instagram: <InstagramIcon />,
	dribbble: <DribbbleIcon />,
	codepen: <CodepenIcon />,
	twitch: <TwitchIcon />,
	linkedin: <LinkedInIcon />,
	behance: <BehanceIcon />,
} satisfies Record<Socials, React.ReactElement>

function ProfileCard({ person }: { person: Person }) {
	return (
		<div id={person.id} className="relative flex w-full scroll-mt-24 flex-col">
			<div className="mb-8 aspect-square w-full flex-none">
				<img
					{...getImgProps(getImageBuilder(person.mediaId, person.name), {
						className: 'rounded-lg object-contain',
						widths: [280, 560, 840, 1100, 1300, 1650],
						sizes: [
							'(max-width:639px) 80vw',
							'(min-width:640px) and (max-width:1023px) 40vw',
							'(min-width:1024px) and (max-width:1620px) 25vw',
							'410px',
						],
						transformations: {
							fit: 'pad',
							aspectRatio: '1:1',
						},
					})}
				/>
			</div>

			<div className="flex-auto">
				<div className="mb-4 text-xl font-medium text-slate-500 lowercase">
					{person.role}
				</div>
				<div className="mb-6 flex items-center gap-2">
					<a
						href={`#${person.id}`}
						aria-label={`Permalink to contributor ${person.id}`}
						tabIndex={0}
						className="focus-ring text-secondary hover:text-primary focus:text-primary rounded-sm focus:outline-none"
					>
						#
					</a>
					<H3 className="mb-0">{person.name}</H3>
				</div>
				<Paragraph className="mb-8">{person.description}</Paragraph>
			</div>

			<div className="text-secondary flex flex-none space-x-4">
				{Object.entries(icons).map(([key, Icon]) => {
					const url = person[key as Socials]
					return url ? (
						<a
							key={key}
							href={url}
							className="hover:text-primary focus:text-primary"
						>
							{React.cloneElement(Icon, { size: 32 })}
						</a>
					) : null
				})}
			</div>
		</div>
	)
}

function CreditsIndex({ loaderData: data }: Route.ComponentProps) {
	return (
		<>
			<HeroSection
				title="Curious to see all the people who helped out making this website?"
				subtitle="Start scrolling to learn more about everyone involved."
				image={
					<img
						{...getHeroImageProps(images.kentCodingOnCouch, {
							className: 'rounded-lg',
							transformations: {
								fit: 'cover',
								aspectRatio: '3:4',
								gravity: 'auto',
							},
						})}
					/>
				}
				arrowUrl="#intro"
				arrowLabel="Get to know more here"
			/>

			<Grid className="mb-24 lg:mb-64">
				<div className="col-span-full mb-12 lg:col-span-4 lg:mb-0">
					<H6 id="intro">{`Producing this site was a team effort`}</H6>
				</div>
				<div className="col-span-full mb-8 lg:col-span-8 lg:mb-20">
					<H2 className="mb-8">
						{`
              kentcdodds.com is more than just my developer portfolio. It's a
              place for me to share my thoughts, ideas, and experiences as
              well as the thoughts, ideas, and experiences of others (yourself
              included). It's a full fleged–`}
						<a
							target="_blank"
							rel="noreferrer noopener"
							href="https://github.com/kentcdodds/kentcdodds.com"
						>
							open source
						</a>
						{`–web application.`}
					</H2>
					<H2 variant="secondary" as="p">
						<a href="https://egghead.io?af=5236ad">egghead.io</a>
						{`
              and I have collaborated to make this website a
              truly high-quality and delightful learning experience for you and
              others.
            `}
					</H2>
				</div>
				<Paragraph className="lg:mb:0 col-span-full mb-4 lg:col-span-4 lg:col-start-5 lg:mr-12">
					{`
            It would be impossible to list everyone who has contributed to the
            creation of this website (I'd have to list my parents, teachers,
            etc, etc, etc).
          `}
				</Paragraph>
				<Paragraph className="col-span-full lg:col-span-4 lg:col-start-9 lg:mr-12">
					{`
            But hopefully with this page, you can get an idea of the primary
            group of folks who worked to make this site great.
          `}
				</Paragraph>
			</Grid>

			<HeaderSection
				className="mb-16"
				title="Everyone that helped out."
				subTitle="In no particular order."
			/>

			{data.people.length === 0 ? (
				<Grid className="mb-24">
					<div className="col-span-full rounded-lg border border-gray-200 p-8 dark:border-gray-600">
						<H4 as="h2" className="mb-3">
							No credits are available right now.
						</H4>
						<Paragraph className="mb-4">
							We are likely having trouble with our GitHub integration. Please
							try again soon, or browse the content directly on{' '}
							<a
								href={externalLinks.githubRepo}
								target="_blank"
								rel="noreferrer noopener"
								className="text-primary underline"
							>
								GitHub
							</a>
							.
						</Paragraph>
						<ButtonLink variant="primary" to={externalLinks.githubRepo}>
							Open GitHub repo
						</ButtonLink>
					</div>
				</Grid>
			) : (
				<Grid className="gap-y-20 lg:gap-y-32">
					{data.people.map((person) => (
						<div key={person.id} className="col-span-4">
							<ProfileCard person={person} />
						</div>
					))}
				</Grid>
			)}

			<Spacer size="base" />

			<HeaderSection
				title="Shout-outs"
				subTitle="Some other awesome folks"
				className="mb-16"
			/>
			<Grid className="prose prose-light dark:prose-dark gap-y-20 lg:gap-y-32">
				<Paragraph className="col-span-4">
					<a href="https://x.com/ryanflorence">Ryan Florence</a>
					{` and other friends at `}
					<a href="https://remix.run">Remix.run</a>
					{`
            were super helpful as I was figuring out the best way to rewrite my
            website in this new technology with completely new and improved
            features that far exceeded what my website had been previously.
          `}
				</Paragraph>
				<Paragraph className="col-span-4">
					The syntax highlighting theme in blog posts is inspired by{' '}
					<a href="https://x.com/sarah_edo">Sarah Drasner&apos;s</a>{' '}
					<a href="https://github.com/sdras/night-owl-vscode-theme">
						Night Owl
					</a>
					.
				</Paragraph>
				<Paragraph className="col-span-4">
					{`
            To prepare for the launch of this website, a number of terrific
            folks reviewed and
          `}
					<a href="https://github.com/kentcdodds/kentcdodds.com/issues?q=is%3Aissue">
						opened issues
					</a>
					{` and even made `}
					<a href="https://github.com/kentcdodds/kentcdodds.com/pulls?q=is%3Apr">
						pull requests
					</a>
					{` to get it ready for launch. Thank you!`}
				</Paragraph>
			</Grid>
		</>
	)
}

export default CreditsIndex
